import flatten from 'lodash/flatten';
import get from 'lodash/get';

import { getIdFromGid } from '../../utils';
import { multiSelectType } from '../../components/MultiSelectDropdown';
import {
  emptyGlobalProduct,
  emptyGlobalVariant,
} from '../../utils/globalObjects';

export const formatSelectorProduct = (
  product,
  variantId = '',
): { formattedProduct: GlobalProduct; defaultVariant: GlobalVariant } => {
  const image = get(
    product,
    ['featuredImage', 'transformedSrc'],
    get(product, ['featuredImage', 'url'], ''),
  );

  const variants = product.variants?.map((v) =>
    formatVariant(v, image, product),
  );
  const defaultVariant =
    variants.find((v) => v.id === variantId) || variants[0];

  const optionsAndValues = product?.options?.map(({ name, values, id }) => {
    const variantOptions = values?.map((label) => ({
      label,
      id: label,
      type: 'option',
      optionName: name,
      isSelected: isOptionSelected(defaultVariant, { name, value: label }),
    }));
    return [{ label: name, id, type: 'title' }, ...variantOptions];
  });

  const variantsOptions = variants?.map((v) => {
    const isSelected = v.id === defaultVariant.id;
    return transformVariantToMultiSelectOption(v, isSelected);
  });

  const { maxVariantPrice, minVariantPrice } = product.priceRangeV2;
  const priceRange = {
    max: +maxVariantPrice.amount,
    min: +minVariantPrice.amount,
    currency: maxVariantPrice.currencyCode,
  };

  const formattedProduct = {
    ...emptyGlobalProduct,
    id: parseInt(getIdFromGid(product.id) as string),
    title: product.title,
    description: product.description,
    descriptionHtml: product.descriptionHtml,
    image,
    variants,
    variantsOptions,
    productName: product.title,
    options: product.options,
    price: priceRange.max,
    priceMin: priceRange.min,
    discountedPriceMin: product?.discountedPriceMin,
    priceMax: priceRange.max,
    discountedPriceMax: product?.discountedPriceMax,
    priceCurrency: priceRange.currency,
    isBundle: product.metafield?.key === 'isBundle',
    tracksInventory: product.tracksInventory,
    optionsAndValues: flatten(optionsAndValues) as MultiSelectOption[],
  };

  return { formattedProduct, defaultVariant };
};

const formatVariant = (variant, imageSrc, product): GlobalVariant => {
  const price = variant.price;
  const discountedPrice = variant?.discountedPrice;
  const name = variant.title;
  const id = parseInt(getIdFromGid(variant.id) as string);
  const available = variant.availableForSale;
  const selectedOptions = variant.selectedOptions;

  const image = get(
    variant,
    ['image', 'transformedSrc'],
    get(variant, ['image', 'url'], imageSrc),
  );

  return {
    ...emptyGlobalVariant,
    id,
    price,
    discountedPrice,
    compareAtPrice: variant.price,
    name,
    available,
    image,
    selectedOptions,
    title: variant.title,
    productName: product.title,
    tracksInventory: product.tracksInventory,
    productId: parseInt(getIdFromGid(product.id) as string),
    description: product.description,
    descriptionHtml: product.descriptionHtml,
    options: product.options || [],
  };
};

const isOptionSelected = (
  selectedVariant: GlobalVariant,
  option: { name: string; value: string },
): boolean =>
  selectedVariant.selectedOptions?.some(
    ({ name, value }) => name === option.name && value === option.value,
  ) ?? false;

const transformVariantToMultiSelectOption = (
  variant: GlobalVariant,
  isSelected = false,
): MultiSelectOption => ({
  label: variant.name,
  isSelected,
  id: `${variant.id}`,
  type: multiSelectType.OPTION,
});
